import React, { useState, useEffect, useMemo } from 'react';
import { useParams } from 'react-router';
import { Trans, t } from '@lingui/macro';
import { AlertTriangle, Crown, EyeOff, MessageSquare, Search, Trash2, UserCheck, Users, ArrowRightLeft, Loader2 } from 'lucide-react';

import { RoomSimplified } from '../../../models/Room';
import { WorkspaceMember } from '../../../models/Workspace';
import { Fire } from '../../../services/FireClient';
import { UtilApp } from '../../../services/Utils/UtilApp';
import { getDisplayName } from '../../../services/Utils/userDisplayName';
import { My } from '../../../services/My';
import { getRoomStorageStats, RoomStorageStat, formatBytes } from '../../../services/StorageQuotaService';
import { Card, CardContent } from '../../../components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '../../../components/ui/avatar';
import { Badge } from '../../../components/ui/badge';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { useToast } from '../../../components/ui/use-toast';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "../../../components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../../components/ui/dialog";
import { IconUserPlus } from '../../components/FigmaIcons';

const WSAdminRoomsPage: React.FC = () => {
  const { workspaceId } = useParams<{ workspaceId: string }>();
  const [inProgress, setInProgress] = useState(false);
  const [rooms, setRooms] = useState<(RoomSimplified & Partial<RoomStorageStat>)[]>([]);
  const [members, setMembers] = useState<WorkspaceMember[]>([]);
  const [transferDialog, setTransferDialog] = useState<{ open: boolean, roomId: string, roomName: string, userIds: string[] }>({ open: false, roomId: '', roomName: '', userIds: [] });
  const [participantsDialog, setParticipantsDialog] = useState<{ open: boolean, roomId: string, roomName: string }>({ open: false, roomId: '', roomName: '' });
  const [searchQuery, setSearchQuery] = useState('');
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'rooms' | 'onetoone'>('rooms');
  const { toast } = useToast();

  // Firestore Timestamp → Date 변환 헬퍼
  const toDate = (v: any): Date | undefined => {
    if (!v) return undefined;
    if (v instanceof Date) return v;
    if (v?.toDate) return v.toDate(); // Firestore Timestamp
    if (v?.seconds) return new Date(v.seconds * 1000); // plain object {seconds, nanoseconds}
    const d = new Date(v);
    return isNaN(d.getTime()) ? undefined : d;
  };

  // 워크스페이스 멤버 ID Set
  const memberIdSet = useMemo(() => new Set(members.map(m => m.id)), [members]);

  useEffect(() => {
    if (!workspaceId) return;
    // 워크스페이스 전환 시 이전 데이터 초기화
    setRooms([]);
    setMembers([]);
    setSearchQuery('');
    setActiveTab('rooms');
    loadData();
  }, [workspaceId]);

  const loadData = async () => {
    if (!workspaceId) return;
    setInProgress(true);

    try {
      const [roomRes, memberList] = await Promise.all([
        Fire.ws.getWorkspaceRooms({ workspaceId }),
        Fire.ws.getWorkspaceMembers(workspaceId)
      ]);

      const sorted = roomRes.rooms.sort((a, b) => (b.storageUsage || 0) - (a.storageUsage || 0));
      const mapped = sorted.map(r => ({
        ...r,
        usedBytes: r.storageUsage || 0,
        fileCount: r.storageCount || 0
      }));

      setRooms(mapped);
      setMembers(memberList);
    } catch (e: any) {
      // 워크스페이스 전환 중 stale 권한으로 인한 에러 무시
      if (e?.code !== 'permission-denied') {
        console.error(e);
      }
    } finally {
      setInProgress(false);
    }
  };

  const getRoomStats = (room: RoomSimplified) => {
    const memberCount = room.userIds?.filter(uid => memberIdSet.has(uid)).length ?? 0;
    const guestCount = (room.userIds?.length ?? 0) - memberCount;
    const isCreatorNonMember = room.creatorId ? !memberIdSet.has(room.creatorId) : false;
    const creatorUser = room.users?.find(u => u.id === room.creatorId);
    const creatorMember = members.find(m => m.id === room.creatorId);
    const creatorName = creatorUser?.name || creatorMember?.user?.name || null;
    return { memberCount, guestCount, isCreatorNonMember, creatorName };
  };

  const handleTransferCreator = async (roomId: string, newCreatorId: string) => {
    if (!workspaceId) return;
    setActionLoading(`transfer-${newCreatorId}`);
    try {
      const res = await Fire.ws.transferRoomCreator({ roomId, newCreatorId, workspaceId });
      if (res.status === 'SUCCESS') {
        toast({ title: t`Channel creator has been transferred.` });
        setTransferDialog({ open: false, roomId: '', roomName: '', userIds: [] });
        loadData();
      } else {
        toast({ title: t`Failed to transfer channel creator.` });
      }
    } catch (e) {
      toast({ title: t`Failed to transfer channel creator.` });
    } finally {
      setActionLoading(null);
    }
  };

  const handleJoinAsCreator = async (roomId: string) => {
    if (!workspaceId) return;
    setActionLoading(`join-${roomId}`);
    try {
      const res = await Fire.ws.joinRoomAsCreator({ roomId, workspaceId });
      if (res.status === 'SUCCESS') {
        toast({ title: t`You have joined the channel as creator.` });
        loadData();
      } else if (res.status === 'ALREADY_CREATOR') {
        toast({ title: t`You are already the creator of this channel.` });
      } else {
        toast({ title: t`Failed to join channel.` });
      }
    } catch (e) {
      toast({ title: t`Failed to join channel.` });
    } finally {
      setActionLoading(null);
    }
  };

  // 일반 방과 1:1 방 분리 (My Room 제외)
  const groupRooms = useMemo(() => rooms.filter(r => !r.isOneToOne && r.visibility !== 'personal'), [rooms]);
  const oneToOneRooms = useMemo(() => rooms.filter(r => r.isOneToOne), [rooms]);
  const activeRooms = activeTab === 'rooms' ? groupRooms : oneToOneRooms;

  // 검색 필터
  const filteredRooms = useMemo(() => {
    if (!searchQuery.trim()) return activeRooms;
    const q = searchQuery.trim().toLowerCase();
    return activeRooms.filter(r => {
      // 방 이름 검색
      if (r.name?.toLowerCase().includes(q)) return true;
      // 방 참가자 이름 검색
      if (r.users?.some(u => u.name?.toLowerCase().includes(q))) return true;
      // 멤버 이메일 검색
      const roomMemberEmails = members.filter(m => r.userIds?.includes(m.id));
      if (roomMemberEmails.some(m => m.user.email?.toLowerCase().includes(q))) return true;
      return false;
    });
  }, [activeRooms, searchQuery, members]);

  // 방장이 비멤버인 방 개수
  const orphanCount = rooms.filter(r => r.creatorId && !memberIdSet.has(r.creatorId)).length;

  // 참가자 모달에 표시할 방의 유저 목록
  const participantsRoom = useMemo(() => {
    if (!participantsDialog.open) return null;
    return rooms.find(r => r.id === participantsDialog.roomId) || null;
  }, [participantsDialog, rooms]);

  return (
    <div className="w-full p-2 sm:p-4 md:p-6 lg:p-8 space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between gap-2 sm:gap-4 px-2 sm:px-0">
        <div className="flex items-center gap-2 sm:gap-4">
          <div className="hidden sm:block p-3 rounded-xl bg-muted border border-border">
            <MessageSquare className="h-6 w-6 text-card-foreground" />
          </div>
          <div>
            <h2 className="text-lg sm:text-2xl font-bold tracking-tight"><Trans>Workspace Channels</Trans></h2>
            <p className="hidden sm:block text-sm text-muted-foreground"><Trans>View and manage all channels in this workspace.</Trans></p>
          </div>
        </div>
        <span className="text-xs sm:text-sm text-muted-foreground bg-muted px-2 sm:px-3 py-1 sm:py-1.5 rounded-full whitespace-nowrap shrink-0"><Trans>Total {activeRooms.length}</Trans></span>
      </div>

      {/* 탭 */}
      <div className="flex gap-1 mx-2 sm:mx-0 border-b">
        <button
          onClick={() => setActiveTab('rooms')}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
            activeTab === 'rooms'
              ? 'border-primary text-primary'
              : 'border-transparent text-muted-foreground hover:text-foreground'
          }`}
        >
          <Trans>Channels</Trans>
          <span className="ml-1.5 text-xs text-muted-foreground">({groupRooms.length})</span>
        </button>
        <button
          onClick={() => setActiveTab('onetoone')}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
            activeTab === 'onetoone'
              ? 'border-primary text-primary'
              : 'border-transparent text-muted-foreground hover:text-foreground'
          }`}
        >
          <Trans>1:1 Chats</Trans>
          <span className="ml-1.5 text-xs text-muted-foreground">({oneToOneRooms.length})</span>
        </button>
      </div>

      {/* 검색 */}
      {rooms.length > 0 && (
        <div className="relative mx-2 sm:mx-0">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder={t`Enter the name of the channel to search.`}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9 h-9"
          />
        </div>
      )}

      {/* ⚠️ 비멤버 방장 경고 배너 */}
      {orphanCount > 0 && (
        <div className="flex items-center gap-3 px-4 py-3 bg-amber-50 border border-amber-200 rounded-lg mx-2 sm:mx-0">
          <AlertTriangle className="h-5 w-5 text-amber-600 shrink-0" />
          <div className="text-sm">
            <span className="font-medium text-amber-800"><Trans>{orphanCount} channels have non-member creators</Trans></span>
            <span className="text-amber-600 ml-1"><Trans>- Consider transferring the creator role or deleting these channels.</Trans></span>
          </div>
        </div>
      )}

      {inProgress && (
        <div className="py-8 space-y-4 px-2 sm:px-0">
          {[1, 2, 3].map(i => <div key={i} className="h-16 bg-muted animate-pulse rounded-lg" />)}
        </div>
      )}

      {!inProgress && rooms.length === 0 && (
        <div className="py-12 text-center border rounded-lg border-dashed text-muted-foreground bg-muted/50 mx-2 sm:mx-0">
          <Trans>No channels found in this workspace</Trans>
        </div>
      )}

      {/* 검색 결과 없음 */}
      {!inProgress && rooms.length > 0 && filteredRooms.length === 0 && (
        <div className="py-8 text-center border rounded-lg border-dashed text-muted-foreground bg-muted/50 mx-2 sm:mx-0">
          <Trans>No channels found</Trans>
        </div>
      )}

      {/* 모바일 레이아웃 */}
      <div className="sm:hidden divide-y">
        {filteredRooms.map(r => {
          const { memberCount, guestCount, isCreatorNonMember, creatorName } = getRoomStats(r);
          return (
            <div key={r.id} className={`py-3 px-2 ${isCreatorNonMember ? 'bg-amber-50/50' : ''}`}>
              <div className="flex items-center gap-2 min-w-0">
                <span className="font-semibold text-sm truncate flex-1 min-w-0"># {r.name || t`1:1 Chat`}</span>
                {isCreatorNonMember && <AlertTriangle className="w-3.5 h-3.5 text-amber-500 shrink-0" />}
                {r.visibility === 'unlisted' && <EyeOff className="w-3 h-3 text-muted-foreground shrink-0" />}
              </div>
              <div className="flex items-center gap-2 text-meta-sm text-muted-foreground mt-1">
                <span>{toDate(r.created) ? UtilApp.dateStrToYmd(toDate(r.created), My.user.lang) : '-'}</span>
                <span>•</span>
                <span className="flex items-center gap-0.5">
                  <UserCheck className="w-3 h-3" />{memberCount}
                </span>
                {guestCount > 0 && (
                  <>
                    <span>•</span>
                    <span className="text-primary flex items-center gap-0.5">
                      <Users className="w-3 h-3" /><Trans>Guest {guestCount}</Trans>
                    </span>
                  </>
                )}
                {creatorName && (
                  <>
                    <span>•</span>
                    <span className="flex items-center gap-0.5">
                      <Crown className="w-3 h-3" />{creatorName}
                    </span>
                  </>
                )}
              </div>
              {isCreatorNonMember && (
                <div className="mt-2 flex gap-1">
                  <Button size="sm" variant="outline" className="h-6 text-meta-sm px-2"
                    onClick={() => setTransferDialog({ open: true, roomId: r.id, roomName: r.name || t`1:1 Chat`, userIds: r.userIds || [] })}>
                    <ArrowRightLeft className="w-3 h-3 mr-1" /><Trans>Transfer</Trans>
                  </Button>
                  <Button size="sm" variant="outline" className="h-6 text-meta-sm px-2"
                    disabled={actionLoading === `join-${r.id}`}
                    onClick={() => handleJoinAsCreator(r.id)}>
                    {actionLoading === `join-${r.id}` ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <IconUserPlus className="w-3 h-3 mr-1" />}
                    <Trans>Join as Creator</Trans>
                  </Button>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* 데스크탑 레이아웃 */}
      <div className="hidden sm:grid gap-3">
        {filteredRooms.map(r => {
          const usedBytes = (r as any).usedBytes || 0;
          const fileCount = (r as any).fileCount || 0;
          const { memberCount, guestCount, isCreatorNonMember, creatorName } = getRoomStats(r);

          return (
            <Card key={r.id} className={`transition-colors ${isCreatorNonMember ? 'border-amber-200 bg-amber-50/30' : 'hover:bg-muted/50'}`}>
              <CardContent className="p-4">
                <div className="flex items-center justify-between gap-4">
                  <div className="flex flex-col gap-1 min-w-0 overflow-hidden flex-1">
                    <div className="flex items-center gap-2 min-w-0">
                      <span className="font-semibold truncate"># {r.name || t`1:1 Chat`}</span>
                      {r.visibility === 'unlisted' && (
                        <Badge variant="secondary" className="text-meta-sm h-5 px-1.5 gap-1 shrink-0">
                          <EyeOff className="w-3 h-3" /> <Trans>Unlisted</Trans>
                        </Badge>
                      )}
                      {isCreatorNonMember && (
                        <Badge className="bg-amber-100 text-amber-700 border-amber-200 text-meta-sm h-5 px-1.5 gap-1 shrink-0">
                          <AlertTriangle className="w-3 h-3" /> <Trans>Non-member creator</Trans>
                        </Badge>
                      )}
                    </div>
                    <div className="flex items-center gap-4 text-xs text-muted-foreground">
                      <span><Trans>Created {toDate(r.created) ? UtilApp.dateStrToDay(toDate(r.created), My.user.lang) : '-'}</Trans></span>
                      <span className="w-px h-3 bg-muted" />
                      <span className="flex items-center gap-1">
                        <UserCheck className="w-3.5 h-3.5" />
                        <Trans>Members {memberCount}</Trans>
                      </span>
                      {guestCount > 0 && (
                        <>
                          <span className="w-px h-3 bg-muted" />
                          <span className="text-primary flex items-center gap-1">
                            <Users className="w-3.5 h-3.5" />
                            <Trans>Guests {guestCount}</Trans>
                          </span>
                        </>
                      )}
                      {usedBytes > 0 && (
                        <>
                          <span className="w-px h-3 bg-muted" />
                          <span className="font-medium text-primary">{formatBytes(usedBytes)}</span>
                          <span className="w-px h-3 bg-muted" />
                          <span>{fileCount} files</span>
                        </>
                      )}
                      {creatorName && (
                        <>
                          <span className="w-px h-3 bg-muted" />
                          <span className={`flex items-center gap-1 ${isCreatorNonMember ? 'text-amber-600' : ''}`}>
                            <Crown className="w-3.5 h-3.5" />
                            {creatorName}
                          </span>
                        </>
                      )}
                    </div>
                  </div>

                  {/* User Avatars - 클릭 시 참가자 목록 모달 */}
                  <button
                    className="flex items-center gap-2 shrink-0 cursor-pointer hover:opacity-80 transition-opacity rounded-lg p-1 -m-1 hover:bg-muted"
                    onClick={() => setParticipantsDialog({ open: true, roomId: r.id, roomName: r.name || t`1:1 Chat` })}
                    title={t`View all members`}
                  >
                    <div className="flex -space-x-2">
                      {r.users?.slice(0, 5).map(u => (
                        <Avatar key={u.id} className="w-8 h-8 border-2 border-transparent">
                          <AvatarImage src={u.photoUrl ?? undefined} />
                          <AvatarFallback className="text-meta-sm">{getDisplayName(u).substring(0, 1)}</AvatarFallback>
                        </Avatar>
                      ))}
                      {r.users && r.users.length > 5 && (
                        <div className="flex items-center justify-center w-8 h-8 rounded-full border-2 border-transparent bg-muted text-meta-sm font-medium z-10 text-card-foreground">
                          +{r.users.length - 5}
                        </div>
                      )}
                    </div>
                  </button>
                </div>

                {/* 비멤버 방장 액션 버튼 */}
                {isCreatorNonMember && (
                  <div className="mt-3 pt-3 border-t border-amber-200 flex items-center gap-2">
                    <span className="text-xs text-amber-700 flex-1">
                      <Trans>The creator of this channel is not a workspace member.</Trans>
                    </span>
                    <Button size="sm" variant="outline" className="h-7 text-xs"
                      disabled={!!actionLoading}
                      onClick={() => setTransferDialog({ open: true, roomId: r.id, roomName: r.name || t`1:1 Chat`, userIds: r.userIds || [] })}>
                      <ArrowRightLeft className="w-3 h-3 mr-1" />
                      <Trans>Transfer Creator</Trans>
                    </Button>
                    <Button size="sm" variant="outline" className="h-7 text-xs"
                      disabled={actionLoading === `join-${r.id}`}
                      onClick={() => handleJoinAsCreator(r.id)}>
                      {actionLoading === `join-${r.id}` ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <IconUserPlus className="w-3 h-3 mr-1" />}
                      <Trans>Join as Creator</Trans>
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          )
        })}
      </div>

      {/* 방장 교체 다이얼로그 */}
      <Dialog open={transferDialog.open} onOpenChange={(open) => setTransferDialog(prev => ({ ...prev, open }))}>
        <DialogContent className="w-[95vw] sm:max-w-md">
          <DialogHeader>
            <DialogTitle><Trans>Transfer Channel Creator</Trans></DialogTitle>
            <DialogDescription>
              <Trans>Select a workspace member to become the new creator of "{transferDialog.roomName}".</Trans>
            </DialogDescription>
          </DialogHeader>
          <div className="max-h-64 overflow-y-auto space-y-1 py-2">
            {members.filter(m => transferDialog.userIds.includes(m.id)).map(m => (
              <button
                key={m.id}
                onClick={() => handleTransferCreator(transferDialog.roomId, m.id)}
                disabled={!!actionLoading}
                className="w-full flex items-center gap-3 p-2 rounded-lg hover:bg-muted/50 transition-colors text-left"
              >
                <Avatar className="h-8 w-8 shrink-0">
                  <AvatarImage src={m.user.photoUrl || undefined} />
                  <AvatarFallback className="text-xs">{getDisplayName(m.user).substring(0, 1)}</AvatarFallback>
                </Avatar>
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-sm truncate">{getDisplayName(m.user)}</div>
                  <div className="text-xs text-muted-foreground truncate">{m.user.email}</div>
                </div>
                {actionLoading === `transfer-${m.id}` && (
                  <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
                )}
              </button>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      {/* 참가자 목록 모달 */}
      <Dialog open={participantsDialog.open} onOpenChange={(open) => setParticipantsDialog(prev => ({ ...prev, open }))}>
        <DialogContent className="w-[95vw] sm:max-w-md">
          <DialogHeader>
            <DialogTitle>
              <span className="flex items-center gap-2">
                <Users className="w-5 h-5" />
                # {participantsDialog.roomName}
              </span>
            </DialogTitle>
            <DialogDescription>
              <Trans>{participantsRoom?.users?.length || 0} members</Trans>
            </DialogDescription>
          </DialogHeader>
          <div className="max-h-80 overflow-y-auto space-y-1 py-2">
            {participantsRoom?.users?.map(u => {
              const member = members.find(m => m.id === u.id);
              const isCreator = participantsRoom.creatorId === u.id;
              const isMember = memberIdSet.has(u.id);
              return (
                <div key={u.id} className="flex items-center gap-3 p-2 rounded-lg hover:bg-muted/50 transition-colors">
                  <Avatar className="h-8 w-8 shrink-0">
                    <AvatarImage src={u.photoUrl ?? undefined} />
                    <AvatarFallback className="text-xs">{getDisplayName(u).substring(0, 1)}</AvatarFallback>
                  </Avatar>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-sm truncate flex items-center gap-1.5">
                      {getDisplayName(u)}
                      {isCreator && <Crown className="w-3.5 h-3.5 text-amber-500 shrink-0" />}
                    </div>
                    <div className="text-xs text-muted-foreground truncate">{member?.user.email || ''}</div>
                  </div>
                  {!isMember && (
                    <Badge variant="secondary" className="text-meta-sm h-5 px-1.5 shrink-0">
                      <Trans>Guest</Trans>
                    </Badge>
                  )}
                </div>
              );
            })}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default WSAdminRoomsPage;
